import { isArrayEmpty } from 'util/isEmpty';
import { treeToFlatListForSelector } from 'util/flattenTreeWithExpansion';
import {
  selectHasMatch,
  selectQueryParam,
  selectQueryParamObject,
} from 'util/selector/routerSelector';

import { createSelector } from '@reduxjs/toolkit';
import { WBLangCodeEnum } from '@workbuzz/shared-ui';
import {
  selectDemography,
  selectDemographyLoadingState,
  selectGroupedClassifications,
  selectSelectedClassifications,
} from 'reduxStore/demography/selectors';
import {
  selectDepartments,
  selectDepartmentsLoadingState,
  selectSelectedDepartmentIds,
} from 'reduxStore/departments/selectors';
import qs from 'qs';
import { FILTER_COUNT_COMPARE_MODE, ModeType, SummaryCategoryViewMode } from 'register/ModeType';
import {
  selectAdvocacyFilter,
  selectPaginationItemCount,
  selectPaginationPage,
  selectSearchedText,
  selectSelectedOptionId,
  selectSentimentFilter,
  selectTopicFilter,
} from 'reduxStore/comments/selectors';
import { AppRoute } from 'app/route/app';
import { ReportRoute } from 'app/route/report';
import { QueryType } from 'model/QueryType';
import { ReportCategoryQuery } from 'api/ReportCategory.query';
import { queryDto } from 'api/dto/Query.dto';
import { CompilePathQuery } from 'router/compilePath';
import { Department } from 'model/Department';
import { selectAggregationPeriod } from 'reduxStore/filtering/selectors';
import dayjs from 'dayjs';
import {
  selectIsLifecycleProjectKind,
  selectIsOnDemandProjectKind,
  selectIsPulseProjectKind,
} from 'reduxStore/project/selectors';
import { AppState } from 'reduxStore/appStore';
import { OrderBy, SortBy } from 'component/QuestionTableSortable/utils';
import { LoadingStateEnum } from 'model/Resource';

import {
  selectIsReportView,
  selectReportAdvocacy,
  selectReportAnswerLike,
  selectReportOptionId,
} from './reportSelector';

export const selectModeType = createSelector(selectQueryParamObject(QueryType.Mode), (query) =>
  query === ModeType.COMPARED ? ModeType.COMPARED : ModeType.COMBINED
);

export const selectFiltersQuery = createSelector(
  selectGroupedClassifications,
  selectSelectedDepartmentIds,
  (classifications, departments) =>
    '?' +
    qs.stringify(
      {
        [QueryType.Filter]: {
          [QueryType.Classification]: classifications,
          [QueryType.Department]: departments,
        },
      },
      { encode: false }
    )
);

export const aggregationRelativeDateToQueryFormat = (value: number) =>
  dayjs().add(value, 'month').format('YYYY-MM');

export const selectSliderDatesFromQueryParams = createSelector(
  selectQueryParamObject(QueryType.Filter),
  (filter) => {
    if (!filter) return {};

    const { aggr_from, aggr_to } = filter as any;

    return {
      aggr_from,
      aggr_to,
    };
  }
);

export const selectFormattedSliderDatesFromQueryParams = createSelector(
  selectSliderDatesFromQueryParams,
  (dates) => {
    const formattedFromDate = dayjs(dates.aggr_from).format('MMM YYYY');
    const formattedToDate = dayjs(dates.aggr_to).format('MMM YYYY');

    return {
      formattedFromDate,
      formattedToDate,
    };
  }
);

export const selectLifecycleFiltersQuery = createSelector(
  selectGroupedClassifications,
  selectSelectedDepartmentIds,
  selectAggregationPeriod,
  selectIsReportView,
  selectSliderDatesFromQueryParams,
  (
    classifications,
    departments,
    [aggregationMin, aggregationMax],
    isReport,
    sliderDatesFromQueryParams
  ) =>
    '?' +
    qs.stringify(
      {
        [QueryType.Filter]: {
          [QueryType.Classification]: classifications,
          [QueryType.Department]: departments,
          [QueryType.DateAggregatedFrom]: isReport
            ? sliderDatesFromQueryParams.aggr_from
            : aggregationRelativeDateToQueryFormat(aggregationMin),
          [QueryType.DateAggregatedTo]: isReport
            ? sliderDatesFromQueryParams.aggr_to
            : aggregationRelativeDateToQueryFormat(aggregationMax),
        },
      },
      { encode: false }
    )
);

export const selectLifecycleFiltersReportQuery = createSelector(
  selectGroupedClassifications,
  selectSelectedDepartmentIds,
  selectSliderDatesFromQueryParams,
  (classifications, departments, chartDatesFromQueryParams) =>
    '?' +
    qs.stringify(
      {
        [QueryType.Filter]: {
          [QueryType.Classification]: classifications,
          [QueryType.Department]: departments,
          [QueryType.DateAggregatedFrom]: chartDatesFromQueryParams.aggr_from,
          [QueryType.DateAggregatedTo]: chartDatesFromQueryParams.aggr_to,
        },
      },
      { encode: false }
    )
);

export const selectSummaryCategoryViewMode = createSelector(
  selectQueryParam('categoryMode'),
  (mode): SummaryCategoryViewMode =>
    (mode as SummaryCategoryViewMode) || SummaryCategoryViewMode.Category
);

export const selectIsCategoryModeInUrl = createSelector(
  selectQueryParam('categoryMode'),
  (mode): boolean => mode !== undefined && mode !== null
);

/**
 * Select opinion question table sorting
 */
export const selectSorting = createSelector(
  selectQueryParam('sortColumn'),
  selectQueryParam('sortOrder'),
  (column, order): SortBy => ({ column: column as string, order: order as OrderBy })
);

export const selectLanguage = createSelector(selectQueryParam('language'), (language):
  | string
  | undefined => {
  if (!language) return undefined;
  // Validate against WBLangCodeEnum to prevent arbitrary strings from URL
  const validLanguages = Object.values(WBLangCodeEnum);
  return validLanguages.includes(language as WBLangCodeEnum) ? (language as string) : undefined;
});

export const selectIsReloading = createSelector(
  selectQueryParam('isReloading'),
  (isReloading) => isReloading
);

export const selectTitlePageOnly = createSelector(
  selectQueryParam('titlePageOnly'),
  (titlePageOnly) => titlePageOnly
);

export const selectLegalFiltersAsObject = createSelector(
  selectGroupedClassifications,
  selectSelectedDepartmentIds,
  selectDepartments,
  selectDemography,
  selectDemographyLoadingState,
  selectDepartmentsLoadingState,
  (
    classifications,
    departments: number[],
    legalDepartments: Department[],
    legalDemography,
    demographyLoadingState,
    departmentsLoadingState
  ): CompilePathQuery => {
    // Legal query is the query that only has elements that user's role specifies
    // Only filter if data is RESOLVED or REJECTED to avoid stripping filters during loading
    // When RESOLVED/REJECTED with empty array, filters will be stripped (correct for security)
    const shouldFilterClassifications =
      demographyLoadingState === LoadingStateEnum.RESOLVED ||
      demographyLoadingState === LoadingStateEnum.REJECTED;

    const shouldFilterDepartments =
      departmentsLoadingState === LoadingStateEnum.RESOLVED ||
      departmentsLoadingState === LoadingStateEnum.REJECTED;

    const demographyIdsList = legalDemography.map((elem) => elem.id);
    const filteredClassifications = shouldFilterClassifications
      ? Object.fromEntries(
          Object.entries(classifications).filter(([key]) => demographyIdsList.includes(Number(key)))
        )
      : classifications;

    const flatDepartmentIdsList = treeToFlatListForSelector(legalDepartments).map(
      (elem) => elem.id
    );
    const filteredDepartments = shouldFilterDepartments
      ? departments.filter((department) => flatDepartmentIdsList.includes(department))
      : departments;

    return {
      [QueryType.Filter]: {
        [QueryType.Classification]: filteredClassifications,
        [QueryType.Department]: filteredDepartments,
      },
    };
  }
);

export const selectLegalFiltersWithModeQueryAsObject = createSelector(
  selectGroupedClassifications,
  selectSelectedDepartmentIds,
  selectModeType,
  selectDepartments,
  selectDemography,
  selectDemographyLoadingState,
  selectDepartmentsLoadingState,
  selectSummaryCategoryViewMode,
  selectSorting,
  selectLanguage,
  selectIsReloading,
  (
    classifications,
    departments: number[],
    summaryMode,
    legalDepartments: Department[],
    legalDemography,
    demographyLoadingState,
    departmentsLoadingState,
    categoryMode,
    sortBy,
    language,
    isReloading
  ): CompilePathQuery => {
    // Legal query is the query that only has elements that user's role specifies
    // Only filter if data is RESOLVED or REJECTED to avoid stripping filters during loading
    // When RESOLVED/REJECTED with empty array, filters will be stripped (correct for security)
    const shouldFilterClassifications =
      demographyLoadingState === LoadingStateEnum.RESOLVED ||
      demographyLoadingState === LoadingStateEnum.REJECTED;

    const shouldFilterDepartments =
      departmentsLoadingState === LoadingStateEnum.RESOLVED ||
      departmentsLoadingState === LoadingStateEnum.REJECTED;

    const demographyIdsList = legalDemography.map((elem) => elem.id);
    const filteredClassifications = shouldFilterClassifications
      ? Object.fromEntries(
          Object.entries(classifications).filter(([key]) => demographyIdsList.includes(Number(key)))
        )
      : classifications;

    const flatDepartmentIdsList = treeToFlatListForSelector(legalDepartments).map(
      (elem) => elem.id
    );
    const filteredDepartments = shouldFilterDepartments
      ? departments.filter((department) => flatDepartmentIdsList.includes(department))
      : departments;

    return {
      [QueryType.Filter]: {
        [QueryType.Classification]: filteredClassifications,
        [QueryType.Department]: filteredDepartments,
      },
      mode: summaryMode,
      categoryMode,
      sortColumn: sortBy.column,
      sortOrder: sortBy.order,
      language,
      isReloading,
    };
  }
);

export const selectLegalFiltersWithModeQuery = createSelector(
  selectLegalFiltersWithModeQueryAsObject,
  (queryObject): string => qs.stringify(queryObject, { encode: false })
);

export const selectProjectSearch = createSelector(
  selectQueryParamObject<any>('f'),
  (filterParams): string | undefined => filterParams?.search
);

export const selectProjectsQuery = (forcePage?: number) =>
  createSelector(selectQueryParam('p'), (paginationParams: any) => {
    const currentPage = forcePage ? forcePage : paginationParams ? paginationParams.index : 1;

    return queryDto.build(queryDto.stringifyPagination(currentPage, 50));
  });
const selectCommentsFilterQueryString = ({
  answerLike,
  optionId,
  advocacyIds,
  isReport,
}: {
  answerLike?: string;
  optionId?: number | null;
  advocacyIds?: number[];
  isReport?: boolean;
}) =>
  createSelector(
    selectGroupedClassifications,
    selectSelectedDepartmentIds,
    selectSentimentFilter,
    selectTopicFilter,
    selectIsLifecycleProjectKind,
    selectAggregationPeriod,
    selectSliderDatesFromQueryParams,
    (
      classifications,
      departments,
      sentiments,
      topics,
      isLifecycleKind,
      [aggregationPeriodMinFromState, aggregationPeriodMaxFromState],
      aggregationPeriodFromQueryParams
    ) => {
      return queryDto.stringifyFilter({
        [QueryType.Classification]: classifications,
        [QueryType.Department]: departments,
        [QueryType.Sentiment]: sentiments,
        [QueryType.Topics]: topics,
        [QueryType.AnswerLike]: answerLike,
        [QueryType.Option]: optionId,
        [QueryType.Advocacy]: advocacyIds,
        [QueryType.DateAggregatedFrom]: isLifecycleKind
          ? isReport
            ? aggregationPeriodFromQueryParams.aggr_from
            : aggregationRelativeDateToQueryFormat(aggregationPeriodMinFromState)
          : undefined,
        [QueryType.DateAggregatedTo]: isLifecycleKind
          ? isReport
            ? aggregationPeriodFromQueryParams.aggr_to
            : aggregationRelativeDateToQueryFormat(aggregationPeriodMaxFromState)
          : undefined,
      });
    }
  );

export const selectCommentReportPageFilterQueryString = createSelector(
  selectReportAnswerLike,
  selectReportOptionId,
  selectReportAdvocacy,
  (appState) => appState,
  (answerLike: any, optionId: any, advocacyIds: any, appState: any) => {
    return selectCommentsFilterQueryString({ answerLike, optionId, advocacyIds, isReport: true })(
      appState
    );
  }
);

export const selectCommentsPageFilterQueryString = createSelector(
  selectSearchedText,
  selectSelectedOptionId,
  selectAdvocacyFilter,
  (appState) => appState as AppState,
  (answerLike: any, optionId: any, advocacyIds: any, appState: any) => {
    return selectCommentsFilterQueryString({ answerLike, optionId, advocacyIds })(appState);
  }
);

export const selectCommentsPaginationQueryString = (isReport?: boolean, page?: number) =>
  createSelector(selectPaginationPage, selectPaginationItemCount, (paginationPage, itemCount) =>
    queryDto.stringifyPagination(
      isReport ? 1 : page ? page : paginationPage,
      isReport ? itemCount : 10
    )
  );

export const selectCommentsQuery = ({
  isReport = false,
  optionId,
  advocacyIds,
  page,
  answerLike,
}: {
  isReport?: boolean;
  optionId?: number;
  advocacyIds?: number[];
  page?: number;
  answerLike?: string;
}) =>
  createSelector(
    selectCommentsPaginationQueryString(isReport, page),
    selectCommentsFilterQueryString({ answerLike, optionId, advocacyIds }),
    (commentsPaginationQueryString, commentsFilterQueryString) =>
      queryDto.build(commentsPaginationQueryString, commentsFilterQueryString)
  );

export const hasSelectedAnyDepartment = createSelector(
  selectSelectedDepartmentIds,
  (departments) => !isArrayEmpty(departments)
);

export const selectHasSelectedOneDepartment = createSelector(
  selectSelectedDepartmentIds,
  (ids) => ids.length === 1
);

export const hasSelectedAnyClassification = createSelector(
  selectSelectedClassifications,
  (classifications) => !isArrayEmpty(classifications)
);

export const selectHasSelectedOneClassification = createSelector(
  selectSelectedClassifications,
  (classifications) => classifications?.length === 1
);

export const selectHasSelectedMoreThanOneDepartment = createSelector(
  selectSelectedDepartmentIds,
  (departments) => departments.length >= FILTER_COUNT_COMPARE_MODE
);

export const selectHasSelectedMoreThanOneDemographyFromOneGroup = createSelector(
  selectGroupedClassifications,
  (classifications) => {
    const classificationKeys = Object.keys(classifications);

    if (!isArrayEmpty(classificationKeys)) {
      return (
        classificationKeys.length === 1 &&
        classifications[classificationKeys[0]].length >= FILTER_COUNT_COMPARE_MODE
      );
    }
  }
);

export const selectIsCompareMode = createSelector(
  selectModeType,
  (mode) => mode && mode === ModeType.COMPARED
);

export const selectHasCrossFilterClassifications = createSelector(
  selectGroupedClassifications,
  (classifications) => {
    const values = Object.values(classifications);
    const countOfSingleElementArrays = values.filter((it) => it.length === 1).length;
    return Object.keys(classifications).length === 2 && countOfSingleElementArrays === 1;
  }
);

export const selectIsCompareModeLock = createSelector(
  selectHasCrossFilterClassifications,
  selectHasSelectedMoreThanOneDemographyFromOneGroup,
  selectHasSelectedMoreThanOneDepartment,
  selectSelectedDepartmentIds,
  selectSelectedClassifications,
  selectIsPulseProjectKind,
  selectIsOnDemandProjectKind,
  selectIsLifecycleProjectKind,
  selectHasMatch(AppRoute.Home.SUMMARY),
  selectHasMatch(AppRoute.Home.Summary.REPORT),
  selectHasMatch(AppRoute.INSIGHTS),
  (
    hasCrossFilterClassifications,
    hasSelectedMoreThanOneClassification,
    hasSelectedMoreThanOneDepartment,
    selectedDepartmentIds,
    selectedClassifications,
    isPulseProjectKind,
    isOnDemandProjectKind,
    isLifecycleProjectKind,
    isSummaryPage,
    isCategoryPage,
    isInsightsPage
  ) => {
    const isRelevantPage = isSummaryPage || isCategoryPage || isInsightsPage;
    const isPulseSummaryOrCategoryPage = isPulseProjectKind && (isSummaryPage || isCategoryPage);
    const isLifecycleSummaryPage = isLifecycleProjectKind && isSummaryPage;
    const noDepartmentSelected = selectedDepartmentIds.length === 0;
    const oneDepartmentSelected = selectedDepartmentIds.length === 1;
    const noClassificationSelected = selectedClassifications.length === 0;
    const oneClassificationSelected = selectedClassifications.length === 1;

    if (isRelevantPage) {
      if (
        // TODO: extend isInsightsPage || isPulseSummaryOrCategoryPage part until all the old endpoints switched to the new ones - https://buzzing.atlassian.net/browse/WB3-1453
        (hasCrossFilterClassifications &&
          noDepartmentSelected &&
          (isInsightsPage ||
            isOnDemandProjectKind ||
            isPulseSummaryOrCategoryPage ||
            isLifecycleSummaryPage)) ||
        (hasSelectedMoreThanOneClassification && oneDepartmentSelected) ||
        (hasSelectedMoreThanOneDepartment && oneClassificationSelected)
      ) {
        return false;
      }
    }
    if (isInsightsPage || isSummaryPage) {
      if (
        (hasSelectedMoreThanOneClassification && noDepartmentSelected) ||
        (hasSelectedMoreThanOneDepartment && noClassificationSelected)
      ) {
        return false;
      }
      if (hasSelectedMoreThanOneClassification && selectedDepartmentIds.length > 0) {
        return true;
      }
    }
    return true;
  }
);

/**
 * Base on this prefix backend restrict departments and demographics returned to specific page.
 * Backend resolve if user have all permission for filtering on specific page
 */
export const selectReportCategoryQuery = createSelector(
  selectHasMatch(AppRoute.Home.SUMMARY),
  selectHasMatch(AppRoute.Home.REPORTS),
  selectHasMatch(AppRoute.INSIGHTS),
  selectHasMatch(AppRoute.COMMENTS),
  selectHasMatch(AppRoute.TOPICS),
  selectHasMatch(ReportRoute.REPORT_PATTERN),
  (
    hasMatchSummary,
    hasMatchReports,
    hasMatchInsights,
    hasMatchComments,
    hasMatchTopics,
    hasMatchReportView
  ) => {
    switch (true) {
      case hasMatchSummary:
      case hasMatchInsights:
        return ReportCategoryQuery.SUMMARY;
      case hasMatchComments:
      case hasMatchTopics:
        return ReportCategoryQuery.COMMENT;
      case hasMatchReports:
      case hasMatchReportView:
      default:
        return undefined;
    }
  }
);
